import React, { useEffect, useState } from 'react';
import { Search, Loader2, Plus, ExternalLink, Filter, AlertTriangle, CalendarPlus, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { 
  generateGoogleCalendarUrl, 
  generateICSContent, 
  generateICSDataUri 
} from '../../utils/calendarUtils';

// We no longer track individual applications, only interviews and offers
// Therefore, we do not enforce application limits here.

export default function AdminApplications() {
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // New application state
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newApp, setNewApp] = useState({
    user_id: '',
    company: '',
    role: '',
    location: '',
    job_url: '',
    status: 'Interview' // Default to Interview
  });

  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Filter
  const [filterStatus, setFilterStatus] = useState('all');

  const [notification, setNotification] = useState({ type: '', text: '' });
  const showNotification = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => {
      setNotification(prev => prev.text === text ? { type: '', text: '' } : prev);
    }, 5000);
  };

  // Interview Scheduling Modal State
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [interviewAppId, setInterviewAppId] = useState(null);
  const [interviewDetails, setInterviewDetails] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '',
    link: '',
    type: 'Video Call',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: appsData, error: appsError } = await supabase
        .from('applications')
        .select('*')
        .order('applied_at', { ascending: false });

      if (appsError) throw appsError;

      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, target_role, subscription_plan');

      if (usersError) throw usersError;

      setUsers(usersData || []);
      
      const appsWithUsers = (appsData || []).map(app => {
        const user = usersData.find(u => u.id === app.user_id);
        return {
          ...app,
          user_name: user ? (`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Client') : 'Client'
        };
      });

      setApplications(appsWithUsers);
    } catch (err) {
      console.error('Error fetching applications data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appId, newStatus) => {
    if (newStatus === 'Interview') {
      setInterviewAppId(appId);
      setShowInterviewModal(true);
      return; // Stop here, wait for modal submission
    }

    // Normal status update
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', appId);

      if (error) throw error;

      setApplications(applications.map(app => 
        app.id === appId ? { ...app, status: newStatus } : app
      ));

      // Send notification if status changed to Offered
      if (newStatus === 'Offered') {
        const app = applications.find(a => a.id === appId);
        if (app) {
          await supabase.from('messages').insert({
            user_id: app.user_id,
            message: `🏆 Congratulations! You've received an offer from ${app.company} for the ${app.role} position! Please check your email for the offer details. Let us know if you need help with salary negotiation!`,
            is_from_admin: true,
            admin_has_replied: true,
          });

          // Send real email
          try {
            await supabase.functions.invoke('send-email', {
              body: { 
                user_id: app.user_id, 
                subject: '🏆 Job Offer Received!', 
                html: `
                  <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155;">
                    <h2 style="color: #4F46E5;">Congratulations!</h2>
                    <p>Great news! You have received a job offer from <strong>${app.company}</strong> for the <strong>${app.role}</strong> position.</p>
                    <p>Please check your emails or reach out to us in the Dashboard Messages if you need help negotiating your salary or evaluating the offer.</p>
                    <br/>
                    <p>Best regards,<br/>The Placement Point Team</p>
                  </div>
                `
              }
            });
          } catch (emailErr) {
            console.error('Failed to send email:', emailErr);
          }

          // Send push notification to user
          supabase.functions.invoke('send-push', {
            body: {
              user_id: app.user_id,
              title: '🏆 Job Offer Received!',
              body: `Congratulations! You've received an offer from ${app.company} for the ${app.role} position!`,
              url: '/dashboard'
            }
          }).catch(e => console.error('Error triggering push:', e));
        }
      }
    } catch (err) {
      console.error('Error updating status:', err);
      showNotification('error', 'Failed to update status.');
    }
  };

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    if (!interviewAppId || !interviewDetails.date) return;

    try {
      const updateData = { 
        status: 'Interview',
        interview_date: interviewDetails.date,
        interview_time: interviewDetails.time,
        interview_link: interviewDetails.link,
        interview_notes: interviewDetails.notes || null,
      };

      const { error } = await supabase
        .from('applications')
        .update(updateData)
        .eq('id', interviewAppId);

      if (error) throw error;

      // Update local state
      setApplications(applications.map(app => 
        app.id === interviewAppId ? { ...app, ...updateData } : app
      ));

      // Send notification
      const app = applications.find(a => a.id === interviewAppId);
      if (app) {
        // Generate Google Calendar Link & ICS content
        const eventTitle = `Interview: ${app.role} at ${app.company}`;
        const eventDesc = `Placement Point shortlisted interview.\nType: ${interviewDetails.type}\nNotes: ${interviewDetails.notes || 'No extra preparation notes provided.'}`;
        
        const googleCalUrl = generateGoogleCalendarUrl({
          title: eventTitle,
          date: interviewDetails.date,
          time: interviewDetails.time,
          location: interviewDetails.link,
          description: eventDesc
        });

        const icsContent = generateICSContent({
          title: eventTitle,
          date: interviewDetails.date,
          time: interviewDetails.time,
          location: interviewDetails.link,
          description: eventDesc
        });

        const icsDataUri = generateICSDataUri(icsContent);

        let msg = `🎉 Great news! Your application to ${app.company} for the ${app.role} position has been shortlisted for an interview!\n\n📅 Date: ${interviewDetails.date}`;
        if (interviewDetails.time) msg += `\n⏰ Time: ${interviewDetails.time}`;
        msg += `\n💬 Type: ${interviewDetails.type}`;
        if (interviewDetails.link) msg += `\n📍 Link/Location: ${interviewDetails.link}`;
        if (interviewDetails.notes) msg += `\n📝 Preparation Tips: ${interviewDetails.notes}`;
        msg += `\n\n📅 Save to your Google Calendar:\n🔗 ${googleCalUrl}`;

        await supabase.from('messages').insert({
          user_id: app.user_id,
          message: msg,
          is_from_admin: true,
          admin_has_replied: true,
        });

        // Send real email
        try {
          await supabase.functions.invoke('send-email', {
            body: { 
              user_id: app.user_id, 
              subject: '🎉 Interview Scheduled!', 
              html: `
                <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155; border: 1px solid #E2E8F0; padding: 24px; border-radius: 16px;">
                  <h2 style="color: #4F46E5; margin-top: 0;">Interview Scheduled!</h2>
                  <p>Great news! Your application to <strong>${app.company}</strong> for the <strong>${app.role}</strong> position has been shortlisted for an interview.</p>
                  
                  <div style="background-color: #F8FAFC; padding: 16px; border-radius: 12px; margin: 20px 0;">
                    <p style="margin: 4px 0;"><strong>📅 Date:</strong> ${interviewDetails.date}</p>
                    ${interviewDetails.time ? `<p style="margin: 4px 0;"><strong>⏰ Time:</strong> ${interviewDetails.time}</p>` : ''}
                    <p style="margin: 4px 0;"><strong>💬 Type:</strong> ${interviewDetails.type}</p>
                    ${interviewDetails.link ? `<p style="margin: 4px 0;"><strong>📍 Location/Link:</strong> <a href="${interviewDetails.link}" style="color: #4F46E5;">${interviewDetails.link}</a></p>` : ''}
                  </div>

                  ${interviewDetails.notes ? `
                  <div style="border-left: 4px solid #F59E0B; padding-left: 12px; margin: 20px 0;">
                    <p style="margin: 0; font-weight: bold; color: #D97706;">📝 Preparation Notes & Tips:</p>
                    <p style="margin: 4px 0; font-style: italic;">"${interviewDetails.notes}"</p>
                  </div>
                  ` : ''}

                  <p>Save this interview session directly to your calendar to stay on track:</p>
                  <div style="margin: 24px 0; display: flex; gap: 12px;">
                    <a href="${googleCalUrl}" target="_blank" style="background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; font-weight: bold; border-radius: 8px; display: inline-block;">Add to Google Calendar</a>
                    <a href="${icsDataUri}" download="interview-invite.ics" style="background-color: #10B981; color: white; padding: 12px 24px; text-decoration: none; font-weight: bold; border-radius: 8px; display: inline-block;">Download .ics Invite</a>
                  </div>

                  <p>Prepare well, we're rooting for you!</p>
                  <br/>
                  <p>Best regards,<br/>The Placement Point Team</p>
                </div>
              `
            }
          });
        } catch (emailErr) {
          console.error('Failed to send email:', emailErr);
        }

        // Send push notification to user
        supabase.functions.invoke('send-push', {
          body: {
            user_id: app.user_id,
            title: '🎉 Interview Scheduled!',
            body: `Your interview with ${app.company} has been scheduled for ${interviewDetails.date}.`,
            url: '/dashboard/calendar'
          }
        }).catch(e => console.error('Error triggering push:', e));
      }

      setShowInterviewModal(false);
      setInterviewAppId(null);
      setInterviewDetails({ date: new Date().toISOString().split('T')[0], time: '', link: '', type: 'Video Call', notes: '' });
    } catch (err) {
      console.error('Error scheduling interview:', err);
      showNotification('error', 'Failed to schedule interview.');
    }
  };



  // Open interview scheduling modal for any application (new or existing)
  const openInterviewScheduler = (appId) => {
    setInterviewAppId(appId);
    setInterviewDetails({ date: new Date().toISOString().split('T')[0], time: '', link: '', type: 'Video Call', notes: '' });
    setShowInterviewModal(true);
  };

  const handleAddApplication = async (e) => {
    e.preventDefault();
    if (!newApp.user_id || !newApp.company || !newApp.role) return;

    setAdding(true);

    try {
      const { data, error } = await supabase
        .from('applications')
        .insert([{
          user_id: newApp.user_id,
          company: newApp.company,
          role: newApp.role,
          location: newApp.location,
          job_url: newApp.job_url,
          status: newApp.status,
          applied_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      // Send notification message to user about the new interview/offer
      await supabase.from('messages').insert({
        user_id: newApp.user_id,
        message: `🎉 Great News!\n\nYou have a new update from ${newApp.company} for the ${newApp.role} position! Status: ${newApp.status}. Check your Dashboard for details!`,
        is_from_admin: true,
        admin_has_replied: true,
      });

      // Send real email notification to user
      try {
        await supabase.functions.invoke('send-email', {
          body: { 
            user_id: newApp.user_id, 
            subject: `🎉 New Application Update — ${newApp.company}`, 
            html: `
              <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155; border: 1px solid #E2E8F0; padding: 24px; border-radius: 16px;">
                <h2 style="color: #4F46E5; margin-top: 0;">Great News! 🎉</h2>
                <p>You have a new update on your application:</p>
                
                <div style="background-color: #F8FAFC; padding: 16px; border-radius: 12px; margin: 20px 0;">
                  <p style="margin: 4px 0;"><strong>🏢 Company:</strong> ${newApp.company}</p>
                  <p style="margin: 4px 0;"><strong>💼 Role:</strong> ${newApp.role}</p>
                  ${newApp.location ? `<p style="margin: 4px 0;"><strong>📍 Location:</strong> ${newApp.location}</p>` : ''}
                  <p style="margin: 4px 0;"><strong>📋 Status:</strong> <span style="color: ${newApp.status === 'Interview' ? '#7C3AED' : '#059669'}; font-weight: bold;">${newApp.status}</span></p>
                </div>

                ${newApp.status === 'Interview' ? `
                <div style="border-left: 4px solid #7C3AED; padding-left: 12px; margin: 20px 0;">
                  <p style="margin: 0; color: #7C3AED; font-weight: bold;">📅 Interview details coming soon!</p>
                  <p style="margin: 4px 0; font-size: 14px; color: #64748B;">We'll send you the exact date, time, and meeting link shortly. Keep an eye on your Dashboard calendar.</p>
                </div>
                ` : ''}

                <p>Log in to your Dashboard for full details and next steps.</p>
                <br/>
                <p>Best regards,<br/>The Placement Point Team</p>
              </div>
            `
          }
        });
      } catch (emailErr) {
        console.error('Failed to send email notification:', emailErr);
      }

      // Send push notification to user
      supabase.functions.invoke('send-push', {
        body: {
          user_id: newApp.user_id,
          title: '🎉 Application Status Update',
          body: `Your application to ${newApp.company} for the ${newApp.role} has been updated. Status: ${newApp.status}.`,
          url: '/dashboard'
        }
      }).catch(e => console.error('Error triggering push:', e));

      // Add to UI
      const user = users.find(u => u.id === newApp.user_id);
      const newAppWithUser = {
        ...data,
        user_name: user ? `${user.first_name} ${user.last_name}` : 'Unknown Client'
      };

      setApplications([newAppWithUser, ...applications]);
      setNewApp({ user_id: '', company: '', role: '', location: '', job_url: '', status: 'Interview' });
      setClientSearchQuery('');
      setShowAddModal(false);

      // If status is Interview, immediately open the scheduling modal for the new application
      if (newApp.status === 'Interview' && data?.id) {
        openInterviewScheduler(data.id);
      }
    } catch (err) {
      console.error('Error adding application:', err);
      showNotification('error', 'Failed to add application.');
    } finally {
      setAdding(false);
    }
  };

  const statuses = ['Interview', 'Offered'];

  const filteredApps = applications.filter(app => {
    const matchesSearch = app.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.user_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-indigo-950">Application Tracker</h1>
          <p className="mt-1 text-slate-500">Manage job applications for all clients.</p>
        </div>
        
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" /> Add Interview / Offer
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search company, role, or client..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-indigo-500"
          >
            <option value="all">All Status</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {notification.text && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 transition-all duration-300 animate-fadeIn ${
          notification.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-rose-50 border-rose-200 text-rose-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 text-sm font-medium pr-8 leading-relaxed">{notification.text}</div>
          <button onClick={() => setNotification({ type: '', text: '' })} className="text-slate-400 hover:text-slate-600 flex-shrink-0 focus:outline-none transition-colors">
            <span className="text-xl font-bold leading-none">&times;</span>
          </button>
        </div>
      )}

      {/* Applications Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="p-4">Client</th>
                  <th className="p-4">Company</th>
                  <th className="p-4">Role</th>
                  <th className="p-4">Location</th>
                  <th className="p-4">Date</th>
                  <th className="p-4 text-center">Status</th>
                  <th className="p-4 text-center">Interview</th>
                  <th className="p-4 text-right">Link</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-semibold text-indigo-900 whitespace-nowrap">
                      {app.user_name}
                    </td>
                    <td className="p-4 font-medium text-slate-900 whitespace-nowrap">{app.company}</td>
                    <td className="p-4 text-slate-600">{app.role}</td>
                    <td className="p-4 text-slate-500 whitespace-nowrap">{app.location || '—'}</td>
                    <td className="p-4 text-slate-500 whitespace-nowrap">
                      {new Date(app.applied_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-center">
                      <select
                        value={app.status}
                        onChange={(e) => handleStatusChange(app.id, e.target.value)}
                        className={`px-2.5 py-1 text-xs font-bold rounded-full border-0 cursor-pointer appearance-none text-center ${
                          app.status === 'Interview' ? 'bg-purple-100 text-purple-700' :
                          'bg-green-100 text-green-700'
                        }`}
                      >
                        {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-4 text-center">
                      {app.status === 'Interview' && (
                        <button
                          onClick={() => openInterviewScheduler(app.id)}
                          title={app.interview_date ? 'Reschedule Interview' : 'Schedule Interview'}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
                        >
                          <CalendarPlus className="w-3.5 h-3.5" />
                          {app.interview_date ? 'Reschedule' : 'Schedule'}
                        </button>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {app.job_url ? (
                        <a href={app.job_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredApps.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      No applications found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Application Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="text-lg font-bold text-slate-900">Add New Application</h3>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setClientSearchQuery('');
                  setNewApp({ user_id: '', company: '', role: '', location: '', job_url: '', status: 'Interview' });
                }} 
                className="text-slate-400 hover:text-slate-600 text-xl"
              >&times;</button>
            </div>

            <form onSubmit={handleAddApplication} className="p-6 space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
                <input
                  type="text"
                  required={!newApp.user_id}
                  value={clientSearchQuery}
                  onChange={(e) => {
                    setClientSearchQuery(e.target.value);
                    setShowClientDropdown(true);
                    setNewApp({ ...newApp, user_id: '' }); // Clear selection if they type
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Search for a client..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                
                {/* Overlay to close dropdown when clicking outside */}
                {showClientDropdown && (
                  <div 
                    className="fixed inset-0 z-0" 
                    onClick={() => setShowClientDropdown(false)}
                  ></div>
                )}

                {showClientDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {users
                      .filter(u => `${u.first_name} ${u.last_name}`.toLowerCase().includes(clientSearchQuery.toLowerCase()))
                      .map(u => {
                        const plan = u.subscription_plan || 'free';
                        return (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setNewApp({ ...newApp, user_id: u.id });
                              setClientSearchQuery(`${u.first_name} ${u.last_name}`);
                              setShowClientDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors border-b last:border-0 border-slate-100"
                          >
                            <div className="font-medium text-slate-900">{u.first_name} {u.last_name}</div>
                            <div className="text-xs text-slate-500 capitalize">{plan} plan</div>
                          </button>
                        );
                      })}
                    {users.filter(u => `${u.first_name} ${u.last_name}`.toLowerCase().includes(clientSearchQuery.toLowerCase())).length === 0 && (
                      <div className="p-3 text-sm text-slate-500 text-center">No clients found</div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company *</label>
                  <input
                    required
                    value={newApp.company}
                    onChange={(e) => setNewApp({ ...newApp, company: e.target.value })}
                    placeholder="e.g. Google"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                  <input
                    required
                    value={newApp.role}
                    onChange={(e) => setNewApp({ ...newApp, role: e.target.value })}
                    placeholder="e.g. Software Engineer"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                  <input
                    value={newApp.location}
                    onChange={(e) => setNewApp({ ...newApp, location: e.target.value })}
                    placeholder="e.g. Remote, UK"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Job URL</label>
                  <input
                    value={newApp.job_url}
                    onChange={(e) => setNewApp({ ...newApp, job_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setClientSearchQuery('');
                    setNewApp({ user_id: '', company: '', role: '', location: '', job_url: '', status: 'Interview' });
                  }}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-lg font-bold text-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Interview Scheduling Modal */}
      {showInterviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-violet-50">
              <h3 className="text-lg font-bold text-violet-900">Schedule Interview</h3>
              <button onClick={() => setShowInterviewModal(false)} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
            </div>

            <form onSubmit={handleScheduleInterview} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Interview Date *</label>
                <input
                  type="date"
                  required
                  value={interviewDetails.date}
                  onChange={(e) => setInterviewDetails({ ...interviewDetails, date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Time (Optional)</label>
                <input
                  type="time"
                  value={interviewDetails.time}
                  onChange={(e) => setInterviewDetails({ ...interviewDetails, time: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Interview Type *</label>
                <select
                  required
                  value={interviewDetails.type}
                  onChange={(e) => setInterviewDetails({ ...interviewDetails, type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                >
                  <option value="Video Call">Video Call (Google Meet/Zoom)</option>
                  <option value="Phone Call">Phone Call</option>
                  <option value="In-Person">In-Person Interview</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Meeting Link or Location *</label>
                <input
                  type="text"
                  required
                  value={interviewDetails.link}
                  onChange={(e) => setInterviewDetails({ ...interviewDetails, link: e.target.value })}
                  placeholder="e.g. Google Meet link or Office Address"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Preparation Tips / Candidate Notes (Optional)</label>
                <textarea
                  value={interviewDetails.notes}
                  onChange={(e) => setInterviewDetails({ ...interviewDetails, notes: e.target.value })}
                  placeholder="Describe dress expectations, research tips, strategizing keys..."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInterviewModal(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-lg font-bold text-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-violet-600 text-white rounded-lg font-bold text-sm hover:bg-violet-700 transition-colors flex items-center justify-center"
                >
                  Confirm Interview
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
